import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createDraftCpd, getCpdChatId } from 'shared/actions/cpd';
import { useCpdTracking } from 'shared/containers/valuationTraps/utils/tracking/cpd';
import { selectFirstAnyPhone, selectIsAuthenticated, selectOffer } from 'shared/selectors';
import {
  selectCpdCianSource,
  selectCpdDraftId,
  selectCpdDraftStatus,
  selectCpdPrelandingModalState,
} from 'shared/selectors/cpd/cpd';

export const useCPDProps = () => {
  const modalState = useSelector(selectCpdPrelandingModalState);
  const cpdDraftStatus = useSelector(selectCpdDraftStatus);
  const cpdDraftId = useSelector(selectCpdDraftId);
  const cianSource = useSelector(selectCpdCianSource);
  const offer = useSelector(selectOffer);

  const isUserAuthorized = useSelector(selectIsAuthenticated);
  const userPhone = useSelector(selectFirstAnyPhone);

  const [phoneValue, setPhoneValue] = React.useState(userPhone ?? null);

  const [isMarketingAgreement, toggleMarketingAgreement] = React.useState<boolean>(false);

  const dispatch = useDispatch();

  const { trackCPDGoToChatClick, trackCPDCreateDraftClick } = useCpdTracking();

  const onPhoneInputChange = React.useCallback((value: string) => {
    setPhoneValue(value);
  }, []);

  const onCreateDraftCpd = React.useCallback(async () => {
    trackCPDCreateDraftClick();
    await dispatch(
      createDraftCpd({
        phone: phoneValue,
        legalAgreementAccepted: isMarketingAgreement,
      }),
    );
  }, [dispatch, phoneValue, isMarketingAgreement]);

  const onGoCianChat = React.useCallback(
    async (draftId?: number) => {
      trackCPDGoToChatClick(draftId);

      const chatId = await dispatch(getCpdChatId());

      const url = chatId ? `https://www.cian.ru/dialogs/?chatId=${chatId}` : 'https://www.cian.ru/dialogs/?';
      window.open(url);
    },
    [dispatch],
  );

  const handleChangeMarketingAgreement = React.useCallback(() => {
    toggleMarketingAgreement(!isMarketingAgreement);
  }, [isMarketingAgreement, toggleMarketingAgreement]);

  return {
    isModalOpen: modalState?.isOpen || false,
    modalView: modalState?.modalView || null,
    cianSource,
    isUserAuthorized,
    cpdDraftStatus,
    cpdDraftId,
    offer,
    phoneValue,
    onPhoneInputChange,
    onCreateDraftCpd,
    onGoCianChat,
    isMarketingAgreement,
    handleChangeMarketingAgreement,
  };
};
